import * as yup from 'yup';
import dayjs from 'dayjs';

export const CandidateSummarySchema = yup.object().shape({
  summary: yup.string().required('write your summary'),
});

// Schema for validation
export const CandidateBasicProfileRule = (disability = false) => yup.object().shape({
  name: yup
    .string()
    .required('Please enter your full name')
    .matches(/^[A-Z a-z]+$/, 'Name can only contain alphabetic characters'),
  city: yup.string().required('Please select your city'),
  sex: yup.string().required('Please select your gender'),
  date_of_birth: yup
    .date()
    .nullable()
    .typeError('Please enter a valid date')
    .required('Please enter a valid date')
    .min(new Date(1900, 0, 1), 'Please enter a valid date of birth')
    .max(dayjs().endOf('day').toDate(), 'Please enter a valid date of birth'),
  disability: disability ? yup.string().required('Please select your disability type') : yup.string().nullable().optional(),
});
/**
 * CandidateEducationSchema is a Yup validation schema for validating a candidate's education details.
 * 
 * The schema validates the following fields:
 * - education_from: A nullable date representing the start date of education. It must be a valid date,
 *   not in the future, and not greater than the end date. It must also be after the candidate's birth date.
 * - education_to: A nullable date representing the end date of education. It must be a valid date,
 *   not in the future, and not before the start date. It must also be after the candidate's birth date.
 * - education_institute_name: A string representing the name of the educational institution. It is required.
 * - learning_format: A string indicating the format of learning. It is required.
 * - `${data}_received`: A string representing the type of education (e.g., degree or certificate) received.
 *   It is required.
 * - field_of_study: A string representing the candidate's area of specialization. It is required.
 * - skills: An array of strings representing the candidate's skills. It must contain at least 3 and no more
 *   than 8 skills.
 * 
 * The schema utilizes Yup for validation and includes various constraints to ensure the fields are filled
 * out correctly and comply with the specified rules.
 */

export const CandidateEducationSchema = (data, dob) =>
  yup.object().shape({
    education_from: yup
      .date()
      .nullable()
      .typeError('Please enter a valid date')
      .required('Please select the start date.')
      .min(dob ? new Date(dob) : new Date(1900, 0, 1), 'Please select a date greater than your birth date.')
      .max(dayjs().endOf('day').toDate(), 'Start date cannot be in the future.')
      .test('is-before', 'Start date cannot be greater than end date', function (value) {
        const { education_to } = this.parent;
        return !education_to || !value || dayjs(value).isBefore(dayjs(education_to));
      }),
    education_to: yup
      .date()
      .nullable()
      .typeError('Please enter a valid date')
      .required('Please select the end date.')
      .min(yup.ref('education_from'), 'End date cannot be earlier than start date.')
      .min(dob ? new Date(dob) : new Date(1900, 0, 1), 'Please select a date greater than your birth date.')
      .max(dayjs().endOf('month').toDate(), 'End date cannot be in the future.'),
    education_institute_name: yup.string().required('Please enter the institution name'),
    learning_format: yup.string().required('Please select learning format.'),
    [`${data}_received`]: yup.string().required(`Please select your ${data}${data === 'certificate' ? ' name' : ''}`),
    field_of_study: yup.string().required('Please enter your area of specialization'),
    skills: yup.array().of(yup.string())
      .required('Please select your skills')
      .min(3, 'Please select at least 3 skills')
      .max(8, 'You can select up to 8 skills'),
  });

/**
 * CandidateWorkHistorySchema is a Yup validation schema for validating a candidate's work history details.
 * 
 * The schema validates the following fields:
 * - work_history_from: A nullable date representing the start date of work history. It must be a valid date,
 *   not in the future, and not before the candidate's birth date.
 * - work_history_to: A nullable date representing the end date of work history. It must be a valid date,
 *   not in the future, and not before the start date. If the end date is not provided, the field is optional.
 * - work_history_location: A string representing the location of the work history. It is required.
 * - organization_name: A string representing the name of the organization the candidate worked for. It is required.
 * - last_job_function: A string representing the job function of the candidate. It is required.
 * - highest_designation: A string representing the highest designation of the candidate. It is required.
 * - job_type: A string indicating the type of job. It is required.
 * - team_size_managed: A string indicating the size of the team the candidate managed. It is required.
 * - industry_sector: A string representing the industry sector the candidate worked in. It is required.
 * - skills: An array of strings representing the candidate's skills. It must contain at least 3 and no more than 8 skills.
 * 
 * The schema utilizes Yup for validation and includes various constraints to ensure the fields are filled
 * out correctly and comply with the specified rules.
 */
export const CandidateWorkHistorySchema = (dob) =>
  yup.object().shape({
    work_history_from: yup
      .date()
      .nullable()
      .typeError('Please enter a valid date')
      .required('Please select the start date.')
      .min(dob ? new Date(dob) : new Date(1900, 0, 1), 'Please select a date greater than your birth date.')
      .max(dayjs().endOf('day').toDate(), 'Start date cannot be in the future.')
      .test('is-before', 'Start date cannot be greater than end date', function (value) {
        const { work_history_to } = this.parent;
        return !work_history_to || !value || dayjs(value).isBefore(dayjs(work_history_to));
      }),
    work_history_to: yup
      .date()
      .when('present', {
        is: true,
        then: () => yup.date().nullable(),
        otherwise: () => yup
          .date()
          .nullable()
          .typeError('Please enter a valid date')
          .required('Please select the end date.')
          .min(yup.ref('work_history_from'), 'End date cannot be earlier than start date.')
          .min(dob ? new Date(dob) : new Date(1900, 0, 1), 'Please select a date greater than your birth date.')
          .max(dayjs().endOf('month').toDate(), 'End date cannot be in the future.'),
      }),
    work_history_location: yup.string().required('Please select your work location'),
    organization_name: yup.string().required('Please enter the company name'),
    last_job_function: yup.string().required('Please enter your job function'),
    highest_designation: yup.string().required('Please enter your job title/designation'),
    job_type: yup.string().required('Please enter job type.'),
    team_size_managed: yup.string().required('Please select direct reportees.'),
    industry_sector: yup.string().required('Please select your industry.'),
    skills: yup.array().of(yup.string())
      .required('Please select your skills')
      .min(3, 'Please select at least 3 skills')
      .max(8, 'You can select up to 8 skills'),
  });

/**
 * A Yup validation schema for validating a candidate's work history for a gap.
 *
 * The schema validates the following fields:
 * - work_history_from: A required date representing the start date of the gap. It must be a valid date
 *   that is not in the future.
 * - work_history_to: A date representing the end date of the gap. If the gap is ongoing, it is
 *   nullable. Otherwise, it must be a valid date that is not earlier than the start date and not
 *   later than the current date.
 * - gap_explaination: A string representing the reason for the employment gap. It must not exceed 25
 *   words.
 * - gap_title: A string representing the title of the employment gap. It must not exceed 50 characters.
 *
 * @param {boolean} present - Whether the gap is ongoing.
 * @returns {Object} A Yup schema for validating a candidate's work history for a gap.
 */
export const CandidateWorkHistoryForGapSchema = (present) =>
  yup.object().shape({
    work_history_from: yup
      .date()
      .required('Please select the start date.')
      .max(dayjs().endOf('day').toDate(), 'Start date cannot be in the future.')
      .test('is-before', 'Start date cannot be greater than end date', function (value) {
        const { work_history_to } = this.parent;
        return !work_history_to || !value || dayjs(value).isBefore(dayjs(work_history_to));
      }),
    work_history_to: present
      ? yup.date().nullable()
      : yup
        .date()
        .required('Please select the end date.')
        .min(yup.ref('work_history_from'), 'End date cannot be earlier than start date.')
        .max(dayjs().endOf('month').toDate(), 'End date cannot be in the future.'),
    gap_explaination: yup
      .string()
      .required('Please provide a reason for your employment gap')
      .test('max-words', 'The explanation must not exceed 25 words', value => {
        const wordCount = value ? value.split(' ').filter(word => word.length > 0).length : 0;
        return wordCount <= 25; // Ensure no more than 25 words
      }),
    gap_title: yup
      .string()
      .required('Please specify the title for your employment gap')
      .max(50, 'The title must not exceed 50 characters')
  });

export const ResumeSchema = yup.object().shape({
  title: yup.string().required('Please enter your resume title.'),
  resume: yup
    .mixed()
    .test('fileSize', 'File size is too large.', (value) => {
      return value?.length && value[0].size <= 1024 * 1024 * 5; // 5MB
    })
    .test('fileType', 'Please select pdf/word file.', (value) => {
      return (
        value?.length &&
        ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(
          value[0].type
        )
      );
    })
    .required('Please upload your resume.'),
});

export const CandidateJobRelatedPreferencesRule = yup.object().shape({
  preferd_job_format: yup.string().required('Please enter your prefered job format.'),
  prefered_location: yup.string().required('Please select your prefered location.'),
  notice_period: yup.string().required('Please select your notice period'),
  reason_for_change: yup.string().required('Please enter reason for change '),
  current_ctc: yup
    .number()
    .typeError('Please enter a valid number')
    .min(0, 'CTC must be above zero')
    .required('Please enter your current ctc.'),
  variable_ctc: yup
    .number()
    .typeError('Please enter a valid number ')
    .min(0, 'CTC must be above zero')
    .required('Please enter your variable ctc.'),
  total_ctc: yup.number().typeError('Please enter a valid number ').required('failed to calculate total ctc.'),
  expected_ctc: yup
    .number()
    .typeError('Please enter a valid number ')
    .min(0, 'CTC must be above zero')
    .required('Please enter your expected ctc.'),
  linkedin_profile: yup.string().url('Invalid URL').required('URL is required'),
});

export const CandidateExpertiseSkillsRule = yup.object().shape({
  domain_experties: yup.string().required('Please select your functional expertise.'),
  area_of_expertise: yup.string().required('Please select your area of expertise.'),
  skills: yup
    .array()
    .of(yup.string())
    .required('Please select your skills.')
    .min(5, 'Please select at least five skills.'),
  responsbility_type: yup.string().required('Please enter your Responsibility.'),
  self_assessment: yup.string().required('Please select your self assessment.'),
  practice_experience: yup.string().required('Please enter your practice experiance.'),
  experties_source: yup.string().required('Please select your experiance source.'),
});

/**
 * Validation schema for candidate job preferences
 * @param {Object} values - Values from the form
 * @param {string} type - Type of job (Full time, Part time, Both)
 * @returns {Object} Yup validation schema
 */
export const CandidateJobPreferencesRule = (values, type) =>
  yup.object().shape({
    job_type: yup.array().of(yup.string()).min(1, 'Please select at least one job type'),
    work_format: yup.array().of(yup.string()).min(1, 'Please select  at least one work format'),
    prefered_location: yup.array().of(yup.string()).min(1, 'Please select at least one location').max(5, 'You can select up to 5 locations only'),
    preferred_job_shifts: yup.array().of(yup.string()).min(1, 'Please select at least one job shift'),
    notice_period: (type?.includes('both') || type?.includes('Full time')) ? yup.string().required('Please select your notice period.') : yup.string().nullable(),
    fixed_ctc: (type?.includes('both') || type?.includes('Full time')) ? yup
      .number()
      .typeError('Please enter a valid number')
      .min(1, 'Fixed compensation must be above zero')
      .required('Please enter your fixed compensation')
      .test('is-valid-number', 'Please enter a valid number', value => {
        if (!value) return true;
        if (/[eE]/.test(value)) return false;
        const num = Number(value);
        return !isNaN(num);
      })
      .test('max-ctc', 'Fixed compensation must not exceed 99 crore', value => {
        if (!value) return true;
        const num = Number(value);
        return num <= 999999999;
      })
      : yup.string().nullable(),
    other_ctc: (type?.includes('both') || type?.includes('Full time')) ? yup.string().nullable()
      .test('max-words', 'It must not exceed 25 words', value => {
        const wordCount = value ? value.split(' ').filter(word => word.length > 0).length : 0;
        return wordCount <= 25;
      }) : yup.string().nullable(),
    hour: (type?.includes('both') || type?.includes('Part time')) ? yup
      .number()
      .typeError('Please enter availability hour.')
      .min(1, 'Availability hour must be above zero')
      .required('Please enter your availability hour')
      .test('is-valid-number', 'Please enter a valid number', value => {
        if (!value) return true;
        if (/[eE]/.test(value)) return false;
        const num = Number(value);
        return !isNaN(num);
      })
      .test('max-ctc', 'Availability hour must not exceed 99', value => {
        if (!value) return true;
        const num = Number(value);
        return num <= 99;
      }) : yup.string().nullable(),
    hourly_rate: (type?.includes('both') || type?.includes('Part time')) ? yup
      .number()
      .typeError('Please enter hourly rate')
      .min(1, 'Hourly rate must be above zero')
      .required('Please enter your hourly rate')
      .test('is-valid-number', 'Please enter a valid number', value => {
        if (!value) return true;
        if (/[eE]/.test(value)) return false;
        const num = Number(value);
        return !isNaN(num);
      })
      .test('max-ctc', 'Hourly rate must not exceed 5 lakh', value => {
        if (!value) return true;
        const num = Number(value);
        return num <= 500000;
      }) : yup.string().nullable(),
    expected_ctc: yup
      .string()
      .nullable()
      .test('is-valid-number', 'Please enter a valid number', value => {
        if (!value) return true;
        if (/[eE]/.test(value)) return false;
        const num = Number(value);
        return !isNaN(num);
      })
      .test('max-ctc', 'Expected annual compensation must not exceed 99 crore', value => {
        if (!value) return true;
        const num = Number(value);
        return num <= 999999999;
      }),
    variable_ctc: yup
      .string()
      .nullable()
      .test('is-valid-number', 'Please enter a valid number', value => {
        if (!value) return true;
        if (/[eE]/.test(value)) return false;
        const num = Number(value);
        return !isNaN(num);
      })
      .test('max-ctc', 'Variable compensation must not exceed 99 crore', value => {
        if (!value) return true;
        const num = Number(value);
        return num <= 999999999;
      }),
    // buyout_period: yup
    //   .string()
    //   .nullable()
    //   .when('is_negotiable', {
    //     is: true,
    //     then: schema => schema.required('Please enter your buyout period.'),
    //     otherwise: schema => schema.nullable(),
    //   }),
  });

export const CandidateBasicContactRule = yup.object().shape({
  email: yup.string().required('Please update email address for contact.'),
  phone_number: yup.string().required('Please update phone number.'),
  linkedinprofile: yup.string().required('Please update linkedin profile url.'),
});
